import { type FC, type PropsWithChildren, useCallback, useState } from 'react';
import { useToggle } from '@direct-frontend/stdlib';
import {
    type IntegrationSettingsFeature,
    type SupportedIntegrationSyncTypes,
    type SupportedIntegrationTypes,
} from '../types';
import { SourceBlockContext } from './SourceBlockContext';

type SourceBlockProviderProps = PropsWithChildren<{
    syncType: SupportedIntegrationSyncTypes;
    features: IntegrationSettingsFeature[];
}>;

export const SourceBlockProvider: FC<SourceBlockProviderProps> = ({ syncType, features, children }) => {
    const {
        value: isModalSettingsCompareByOpen,
        toggleOn: setModalSettingsCompareByOpen,
        toggleOff: setModalSettingsCompareByClose,
    } = useToggle(false);

    const [modalSettingsCompareByOnIntegrationType, setModalSettingsCompareByOnIntegrationType] = useState<
        SupportedIntegrationTypes | undefined
    >(undefined);

    const {
        value: isModalSettingsCompareExtendedByOpen,
        toggleOn: setModalSettingsCompareExtendedByOpen,
        toggleOff: setModalSettingsCompareExtendedByClose,
    } = useToggle(false);

    const openModalSettingsCompareBy = useCallback(
        (integrationType?: SupportedIntegrationTypes) => {
            setModalSettingsCompareByOnIntegrationType(integrationType);
            setModalSettingsCompareByOpen();
        },
        [setModalSettingsCompareByOpen]
    );

    const closeModalSettingsCompareBy = useCallback(() => {
        setModalSettingsCompareByClose();
        setModalSettingsCompareByOnIntegrationType(undefined);
    }, [setModalSettingsCompareByClose]);

    const openModalSettingsCompareExtendedBy = useCallback(
        (integrationType?: SupportedIntegrationTypes) => {
            setModalSettingsCompareByOnIntegrationType(integrationType);
            setModalSettingsCompareExtendedByOpen();
        },
        [setModalSettingsCompareExtendedByOpen]
    );

    const closeModalSettingsCompareExtendedBy = useCallback(() => {
        setModalSettingsCompareExtendedByClose();
        setModalSettingsCompareByOnIntegrationType(undefined);
    }, [setModalSettingsCompareExtendedByClose]);

    return (
        <SourceBlockContext.Provider
            value={{
                syncType,
                features,
                modalSettingsCompareBaseProps: {
                    integrationType: modalSettingsCompareByOnIntegrationType,
                },
                modalSettingsCompareByOn: {
                    isOpen: isModalSettingsCompareByOpen,
                    open: openModalSettingsCompareBy,
                    close: closeModalSettingsCompareBy,
                },

                modalSettingsCompareExtendedByOn: {
                    isOpen: isModalSettingsCompareExtendedByOpen,
                    open: openModalSettingsCompareExtendedBy,
                    close: closeModalSettingsCompareExtendedBy,
                },
            }}
        >
            {children}
        </SourceBlockContext.Provider>
    );
};
